import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, updateUserProfile } from '../../services/supabaseService';
import { useNavigate } from 'react-router-dom';
import { ExtendedUserProfile } from '../../types/user';
import BaseProfilePage from '../../components/mypage/BaseProfilePage';
import { logout } from '../../services/kakaoAuthService';
import {
  inputStyles,
  disabledInputStyles,
  sectionTitleStyles,
  sectionDescriptionStyles,
  buttonStyles,
  stackSpacing,
  sectionSpacing,
} from '../../styles/profileStyles';

const ACCENT_COLOR = '#2962ff';

const AdvertiserEditProfilePage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editedProfile, setEditedProfile] = useState<Partial<ExtendedUserProfile>>({
    displayName: '',
    email: '',
    phoneNumber: '',
    address: {
      zipCode: '',
      address1: '',
      address2: ''
    },
    businessInfo: {
      companyName: '',
      businessNumber: '',
      businessType: '',
      businessAddress: '',
      ceoName: '',
      businessEmail: '',
    }
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (currentUser?.uid) {
          const userProfile = await getUserProfile(currentUser.uid);
          setEditedProfile({
            ...userProfile,
            phoneNumber: userProfile.phoneNumber || '',
            address: userProfile.address || {
              zipCode: '',
              address1: '',
              address2: ''
            },
            businessInfo: userProfile.businessInfo || {
              companyName: '',
              businessNumber: '',
              businessType: '',
              businessAddress: '',
              ceoName: '',
              businessEmail: '',
            }
          });
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setSnackbar({
          open: true,
          message: '프로필을 불러오는데 실패했습니다.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  const handleSave = async () => {
    try {
      setLoading(true);
      if (currentUser) {
        await updateUserProfile(currentUser.uid, {
          ...editedProfile,
          userType: 'advertiser',
          updatedAt: Date.now()
        });

        setSnackbar({
          open: true,
          message: '프로필이 성공적으로 업데이트되었습니다.',
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/mypage/advertiser');
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setSnackbar({
        open: true,
        message: '프로필 업데이트에 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | { zipCode?: string, address1?: string, address2?: string }) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBusinessInfoChange = (field: string, value: string) => {
    setEditedProfile(prev => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        [field]: value
      }
    }));
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1) {
      try {
        setLoading(true);
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('로그아웃 실패:', error);
        setSnackbar({
          open: true,
          message: '로그아웃에 실패했습니다.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    } else if (newValue === 2) {
      const confirmed = window.confirm('정말로 탈퇴하시겠습니까?');
      if (confirmed) {
        try {
          // 회원탈퇴 로직
          navigate('/');
        } catch (error) {
          console.error('회원탈퇴 실패:', error);
        }
      }
    } else {
      setTabValue(newValue);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const renderContent = () => (
    <Stack spacing={sectionSpacing} sx={{ pt: 3, pb: 4 }}>
      {/* 기본정보 섹션 */}
      <Box>
        <Typography sx={sectionTitleStyles}>
          기본정보
        </Typography>
        <Stack spacing={stackSpacing}>
          <TextField
            fullWidth
            label="아이디(이메일)"
            value={editedProfile.email || ''}
            disabled
            sx={disabledInputStyles}
          />
          <TextField
            fullWidth
            label="닉네임"
            value={editedProfile.displayName || ''}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="휴대폰 번호"
            value={editedProfile.phoneNumber || ''}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            sx={inputStyles}
          />
        </Stack>
      </Box>

      <Divider sx={{ borderColor: '#e0e0e0' }} />

      {/* 회사정보 섹션 */}
      <Box>
        <Typography sx={sectionTitleStyles}>
          회사정보
        </Typography>
        <Typography sx={sectionDescriptionStyles}>
          사업자 도용시 법적 처벌을 받을 수 있습니다.
        </Typography>
        <Stack spacing={stackSpacing}>
          <TextField
            fullWidth
            label="사업자명"
            value={editedProfile.businessInfo?.companyName || ''}
            onChange={(e) => handleBusinessInfoChange('companyName', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="브랜드명"
            value={editedProfile.businessInfo?.businessType || ''}
            onChange={(e) => handleBusinessInfoChange('businessType', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="사업자등록번호"
            value={editedProfile.businessInfo?.businessNumber || ''}
            onChange={(e) => handleBusinessInfoChange('businessNumber', e.target.value)}
            helperText="'-' 없이 입력해주세요"
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="대표자명"
            value={editedProfile.businessInfo?.ceoName || ''}
            onChange={(e) => handleBusinessInfoChange('ceoName', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="사업자 이메일"
            value={editedProfile.businessInfo?.businessEmail || ''}
            onChange={(e) => handleBusinessInfoChange('businessEmail', e.target.value)}
            sx={inputStyles}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="우편번호"
              value={editedProfile.address?.zipCode || ''}
              onChange={(e) => {
                const updatedAddress = {
                  ...(editedProfile.address || {}),
                  zipCode: e.target.value
                };
                handleInputChange('address', updatedAddress);
              }}
              sx={inputStyles}
            />
            <Button
              variant="outlined"
              sx={buttonStyles(ACCENT_COLOR)}
              style={{ minWidth: '100px' }}
            >
              찾기
            </Button>
          </Box>
          <TextField
            fullWidth
            label="주소"
            value={editedProfile.address?.address1 || ''}
            onChange={(e) => {
              const updatedAddress = {
                ...(editedProfile.address || {}),
                address1: e.target.value
              };
              handleInputChange('address', updatedAddress);
            }}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="상세주소"
            value={editedProfile.address?.address2 || ''}
            onChange={(e) => {
              const updatedAddress = {
                ...(editedProfile.address || {}),
                address2: e.target.value
              };
              handleInputChange('address', updatedAddress);
            }}
            sx={inputStyles}
          />
        </Stack>
      </Box>

      {/* 저장 버튼 */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        sx={buttonStyles(ACCENT_COLOR)}
      >
        저장
      </Button>
    </Stack>
  );

  return (
    <BaseProfilePage
      title="내 계정"
      backUrl="/mypage/advertiser"
      tabValue={tabValue}
      onTabChange={handleTabChange}
      accentColor={ACCENT_COLOR}
      snackbar={snackbar}
      onSnackbarClose={() => setSnackbar({ ...snackbar, open: false })}
    >
      {renderContent()}
    </BaseProfilePage>
  );
};

export default AdvertiserEditProfilePage; 